import { Link, PrefetchPageLinks, useNavigate } from 'react-router';
import { usePointerActivation } from '@/hooks';
import { Menu } from 'lucide-react';
import { cn, buildAuthRedirectUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import NotificationPopover from '@/components/notifications/NotificationPopover';
import Logo from '@/assets/images/logo/full_logo.svg';
import HomeMobileLogo from '@/assets/icons/main-bottom-navigation/hq-logo.png';
import { PAGES_WITHOUT_NAV } from '@/constants';
import { UsersDto } from '@sportspredict/shared';

interface LayoutHeaderProps {
	isMobile: boolean;
	currentPageName: string;
	user: UsersDto | null | undefined;
	onSidebarOpen: () => void;
	setNotificationsReady: (ready: boolean) => void;
	isCrowdLockOpen: boolean;
	isRegisteredUser: boolean;
	isLightShellPage: boolean;
}

export function LayoutHeader({
	isMobile,
	currentPageName,
	user,
	onSidebarOpen,
	setNotificationsReady,
	isCrowdLockOpen,
	isRegisteredUser,
	isLightShellPage,
}: LayoutHeaderProps) {
	const navigate = useNavigate();
	const sidebarOpenPointerProps = usePointerActivation(onSidebarOpen);

	if (PAGES_WITHOUT_NAV.includes(currentPageName) || isCrowdLockOpen) {
		return null;
	}

	const isLobbyDetailsPage = currentPageName === 'LobbyDetails';

	return (
		<header
			className={cn(
				'px-4 py-4 z-40 ml-0 fixed top-0 w-full',
				isLightShellPage &&
					isMobile &&
					'h-16 border-b-[0.5px] border-[#e1e7f1] bg-white/95 py-3 backdrop-blur-lg',
				isLightShellPage
					? 'lg:absolute lg:left-auto lg:right-16 lg:top-12 lg:w-auto lg:p-0'
					: isLobbyDetailsPage
						? 'lg:absolute lg:top-0 lg:left-64 lg:right-0 lg:w-auto lg:mx-0 lg:px-8 lg:pointer-events-none'
						: 'lg:mx-64 lg:static lg:w-auto'
			)}
		>
			<div className="flex items-center justify-between">
				{isMobile ? (
					<div className="flex items-center">
						<Link to="/" prefetch="viewport">
							<img
								src={isLightShellPage ? HomeMobileLogo : Logo}
								alt="App Logo"
								className={cn(isLightShellPage ? 'h-5 w-44 object-contain' : 'ml-3 mt-1 h-10')}
							/>
						</Link>
					</div>
				) : (
					<div />
				)}

				<div
					className={cn(
						'flex items-center gap-5 lg:pointer-events-auto',
						isLightShellPage && isMobile && 'gap-3'
					)}
				>
					{!isRegisteredUser && !isMobile && (
						<>
							<PrefetchPageLinks page={buildAuthRedirectUrl()} />
							<Button
								onClick={() => navigate(buildAuthRedirectUrl())}
								className={'w-[160px] h-10 rounded-full bg-blue-custom-2  text-[#F7F7F8] font-bold'}
							>
								Login
							</Button>
						</>
					)}

					{user && (
						<NotificationPopover
							setNotificationsReady={setNotificationsReady}
							user={user}
							triggerClassName={cn(
								isLobbyDetailsPage && !isMobile && 'border border-[#B5B6BA]',
								isLightShellPage &&
									isMobile &&
									'size-10! rounded-full! border-[0.5px]! border-[#e4e8f3]! bg-white! p-0! text-[#1c2d4a]! shadow-[0_1px_2px_rgba(65,77,115,0.05)] hover:bg-white/90! [&_svg]:size-5!',
								isLightShellPage &&
									!isMobile &&
									'size-10! bg-white! p-0! text-[#1C2D4A]! shadow-[0_8px_20px_rgba(28,45,74,0.16)] hover:bg-white/90!'
							)}
						/>
					)}

					{isMobile && (
						<Button
							variant="ghost"
							size="icon"
							{...sidebarOpenPointerProps}
							className={cn(
								'h-[48px] w-[48px] rounded-full bg-[#1C2D4A] p-2 text-white',
								isLightShellPage &&
									'size-10! border-[0.5px] border-[#e4e8f3] bg-white! p-0! text-[#1c2d4a]! shadow-[0_1px_2px_rgba(65,77,115,0.05)] hover:bg-white/90!'
							)}
						>
							<Menu className={cn('h-6 w-6', isLightShellPage && 'size-5')} />
						</Button>
					)}
				</div>
			</div>
		</header>
	);
}
